import { useState, useEffect, useCallback } from "react";
import { api, ApiError } from "@/lib/api-client";

interface VendorStatus {
  vendorId: string;
  displayName: string;
  documentCount: number;
  spatial: { fieldCount: number; avgSamples: number };
  patterns: {
    fieldCount: number;
    patternCount: number;
    examples: Array<{ fieldType: string; patternRegex: string }>;
  };
  cooccurrence: { pairCount: number; coveragePercent: number };
}

interface IntelligenceStatus {
  orgId: string;
  lastTrainedAt: string | null;
  retrainThreshold: number | null;
  vendors: VendorStatus[];
}

export function useIntelligence() {
  const [status, setStatus] = useState<IntelligenceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [retraining, setRetraining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get<IntelligenceStatus>("/ocr/intelligence/status");
      setStatus(data);
      setError(null);
    } catch (e) {
      // 401 is handled inside api-client (redirect to Suite SSO); surface other errors.
      if (e instanceof ApiError && e.status === 401) return;
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  const triggerRetrain = useCallback(async () => {
    try {
      setRetraining(true);
      await api.post("/ocr/intelligence/retrain");
      await fetchStatus();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
      setError(e instanceof Error ? e.message : "Retrain failed");
    } finally {
      setRetraining(false);
    }
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { status, loading, retraining, error, triggerRetrain, refetch: fetchStatus };
}
